import { useMemo, useState } from 'react';
import { Button, Card, Icon, StatusPill } from '@hive/ui';
import { api, type AuditEvent, type RoleInfo, type UserAccount, type Venue } from '../api/client';
import { useLoad, useNow } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { Shell } from '../components/Shell';
import { UserDialog } from '../components/UserDialog';
import { ago, initials } from '../format';

type Filter = 'all' | 'admin' | 'tenant_operator' | 'venue_staff' | 'disabled';

/** Users, their roles and venues, and the audit log of changes to them. */
export function UsersPage() {
  const { user: me } = useAuth();
  const now = useNow(30_000);
  const users = useLoad(async () => (await api.GET('/v1/users')).data);
  const roles = useLoad(async () => (await api.GET('/v1/roles')).data);
  const venues = useLoad(async () => (await api.GET('/v1/venues')).data);
  const audit = useLoad(async () => (await api.GET('/v1/audit', { params: { query: { targetType: 'user', limit: 30 } } })).data);
  const [editing, setEditing] = useState<UserAccount | 'new' | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  const venueName = useMemo(() => new Map((venues.data ?? []).map((v: Venue) => [v.id, v.name])), [venues.data]);
  const roleLabel = (role: string) => roles.data?.find((r: RoleInfo) => r.role === role)?.label ?? role;
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users.data ?? []).filter((u) => {
      if (filter === 'disabled' ? !u.disabled : filter !== 'all' && u.role !== filter) return false;
      return !q || [u.name, u.username, u.email, u.title].some((x) => x?.toLowerCase().includes(q));
    });
  }, [users.data, filter, query]);

  const counts = (users.data ?? []).reduce<Record<string, number>>((acc, u) => {
    acc[u.disabled ? 'disabled' : u.role] = (acc[u.disabled ? 'disabled' : u.role] ?? 0) + 1;
    return acc;
  }, {});
  const reloadAll = () => {
    void users.reload();
    void audit.reload();
  };
  const venuesOf = (u: UserAccount) => {
    if (u.allVenues) return 'All venues';
    if (u.venueIds.length === 0) return 'No venues';
    const names = u.venueIds.map((id) => venueName.get(id) ?? 'Unknown venue');
    return names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
  };

  return (
    <Shell network={null} live>
      <main className="mc-page">
        <div className="mc-pagehead">
          <div className="mc-pagehead__left">
            <h1 className="mc-h1">Users</h1>
            <span className="mc-pill-soft">{users.data?.length ?? '…'} people</span>
          </div>
          <div className="mc-pagehead__actions">
            <Button variant="primary" onClick={() => setEditing('new')} disabled={!roles.data || !venues.data}>
              <Icon name="plus" size={18} />
              Add user
            </Button>
          </div>
        </div>

        <div className="mc-admin-grid">
          <Card className="mc-table-card">
            <div className="mc-toolbar">
              <div className="mc-tabs" role="tablist" aria-label="Filter users">
                {(
                  [
                    ['all', 'All', users.data?.length],
                    ['admin', 'Admins', counts.admin],
                    ['tenant_operator', 'Operators', counts.tenant_operator],
                    ['venue_staff', 'Venue staff', counts.venue_staff],
                    ['disabled', 'Disabled', counts.disabled],
                  ] as [Filter, string, number | undefined][]
                ).map(([key, label, n]) => (
                  <button key={key} type="button" role="tab" aria-selected={filter === key} className={`mc-tab${filter === key ? ' mc-tab--on' : ''}`} onClick={() => setFilter(key)}>
                    {label}
                    <span className="mc-tab__count">{n ?? 0}</span>
                  </button>
                ))}
              </div>
              <label className="mc-search__box">
                <Icon name="search" size={16} />
                <span className="hv-sr-only">Find a user</span>
                <input type="search" placeholder="Find by name, username or email" value={query} onChange={(e) => setQuery(e.target.value)} />
              </label>
            </div>
            {users.error && <p className="mc-note mc-note--bad">Could not load users. Refresh to try again.</p>}
            <table className="mc-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Role</th>
                  <th scope="col">Venues</th>
                  <th scope="col">Last signed in</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {list.map((u) => (
                  <tr key={u.id} className={u.disabled ? 'mc-table__row--muted' : undefined}>
                    <td>
                      <button type="button" className="mc-table__link" onClick={() => setEditing(u)}>
                        <span aria-hidden="true" className="mc-user__avatar">
                          {initials(u.name)}
                        </span>
                        <span className="mc-table__who">
                          <span className="mc-strong">
                            {u.name}
                            {u.id === me?.id && <span className="hv-muted"> · you</span>}
                          </span>
                          <span className="mc-small hv-muted">
                            {u.username}
                            {u.title ? ` · ${u.title}` : ''}
                          </span>
                        </span>
                      </button>
                    </td>
                    <td>{roleLabel(u.role)}</td>
                    <td className="mc-table__venues">{venuesOf(u)}</td>
                    <td className="mc-small">{u.lastLoginAt ? `${ago(u.lastLoginAt, now)} ago` : 'Never'}</td>
                    <td>
                      <StatusPill status={u.disabled ? 'offline' : 'healthy'}>{u.disabled ? 'Disabled' : 'Active'}</StatusPill>
                    </td>
                  </tr>
                ))}
                {users.data && list.length === 0 && (
                  <tr>
                    <td colSpan={5} className="mc-table__empty">
                      No users match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>

          <div className="mc-col">
            <Card padded className="mc-side-card">
              <span className="mc-section-title">Roles</span>
              <ul className="mc-rolelist">
                {(roles.data ?? []).map((r) => (
                  <li key={r.role}>
                    <span className="mc-strong">{r.label}</span>
                    <span className="mc-small hv-muted">{r.description}</span>
                    <span className="mc-tiny hv-muted">{r.allVenues ? 'Every venue' : 'Assigned venues only'}</span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card padded className="mc-side-card">
              <span className="mc-section-title">
                <Icon name="history" size={16} />
                &nbsp;Recent changes
              </span>
              <AuditFeed events={audit.data ?? []} now={now} />
            </Card>
          </div>
        </div>
      </main>
      {editing && roles.data && venues.data && (
        <UserDialog
          user={editing === 'new' ? undefined : editing}
          roles={roles.data}
          venues={venues.data}
          selfId={me?.id}
          onClose={() => setEditing(null)}
          onSaved={reloadAll}
        />
      )}
    </Shell>
  );
}

export function AuditFeed({ events, now }: { events: AuditEvent[]; now: number }) {
  if (events.length === 0) return <p className="mc-small hv-muted">No changes yet.</p>;
  return (
    <ul className="mc-feed mc-feed--audit">
      {events.map((e) => (
        <li key={e.id}>
          <span className="mc-feed__time">{ago(e.at, now)}</span>
          <span>
            <span className="mc-strong">{e.actor}</span> · {e.summary}
          </span>
        </li>
      ))}
    </ul>
  );
}
